import cn from 'classnames'
import { type ReactNode, useCallback } from 'react'

import { Translation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../configs/translations'
import { CockpitPartnerScope } from '../components/cockpitPartnerScope'

import { AttentionStatusesBreadcrumb } from './components/attentionStatusesBreadcrumb'
import { AttentionStatusPlaceholder } from './components/attentionStatusPlaceholder'
import { AttentionDetailExitProvider } from './hooks/useAttentionDetailExit'
import { useAttentionStatusesText } from './hooks/useAttentionStatusesText'
import { useCockpitAttentionStatuses } from './hooks/useCockpitAttentionStatuses'
import { useManagerAttentionStatuses } from './hooks/useManagerAttentionStatuses'
import { renderAttentionStatusSection } from './renderAttentionStatusSection'
import type { IAttentionStatusesTile, IAttentionStatusesVariantProps } from './types'
import { AttentionStatusesHero } from './variants/AttentionStatusesHero'

import styles from './CockpitAttentionStatusesExpanded.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

interface IExpandedBodyProps {
  tile: IAttentionStatusesTile
  onCollapse: () => void
  /** The section a status' detail shows, where the cockpit has one for that status' bit. */
  detailSection?: (bitValue: number) => ReactNode
  /** Whose next steps each status lists under it, on the status level. */
  nextSteps?: IAttentionStatusesVariantProps['nextSteps']
}

const ExpandedBody = ({ tile, onCollapse, detailSection, nextSteps }: IExpandedBodyProps) => {
  const text = useAttentionStatusesText()
  const { crumbs, detail, goToLevel, selectRelatedStatus } = tile

  /* Leaving the detail returns to the level it was opened from — and that level is drawn in the
     collapsed tile, so the tile collapses with it. */
  const handleBack = useCallback(() => {
    goToLevel(crumbs.length - 1)
    onCollapse()
  }, [crumbs.length, goToLevel, onCollapse])

  const handleNavigate = useCallback(
    (index: number) => {
      goToLevel(index)
      onCollapse()
    },
    [goToLevel, onCollapse]
  )

  if (detail) {
    const placeholder = (
      <AttentionStatusPlaceholder
        status={detail.status}
        crumbs={crumbs}
        partnerName={detail.partnerName}
        partnerId={detail.partnerId}
        relatedStatuses={detail.relatedStatuses}
        section={detailSection?.(detail.status.bitValue)}
        onBack={handleBack}
        onNavigate={handleNavigate}
        onSelectRelated={selectRelatedStatus}
      />
    )

    return (
      /* The sections take the reader to the place that answers a status; the card is drawn over
         that place, so it is handed its own way out. */
      <AttentionDetailExitProvider onExit={onCollapse}>
        <div className={cn(styles.root, styles.detail)}>
          {/* A detail opened for one partner of the manager cockpit reads that partner alone, as
              the partner's own cockpit would. */}
          {detail.partnerId ? (
            <CockpitPartnerScope
              partnerId={detail.partnerId}
              partnerName={detail.partnerName ?? ''}
              status={{
                bitValue: detail.status.bitValue,
                offering: detail.status.offerings[0] ?? null,
              }}
            >
              {placeholder}
            </CockpitPartnerScope>
          ) : (
            placeholder
          )}
        </div>
      </AttentionDetailExitProvider>
    )
  }

  return (
    /* A step taken from the list goes to a place on the page, so the expanded tile gets out of
       its way as a detail's sections do. */
    <AttentionDetailExitProvider onExit={onCollapse}>
      <div className={styles.root}>
        {crumbs.length > 0 && (
          <AttentionStatusesBreadcrumb
            crumbs={crumbs.slice(0, -1)}
            current={crumbs[crumbs.length - 1].label}
            onNavigate={goToLevel}
          />
        )}

        {/* A detail says what it shows through the placeholder; a list says it here, so no level of
        the expanded tile opens on bare rows. A level the drill-down walked into counts markets or
        partners rather than statuses, so it names its own unit. */}
        <p className={styles.intro}>
          {crumbs.length > 0 ? (
            <Translation span {...text.phrase.levelIntro(tile.unit, tile.items.length)} />
          ) : (
            <Translation span text={KEYS.listIntro} />
          )}
        </p>

        <AttentionStatusesHero
          items={tile.items}
          totalCount={tile.totalCount}
          unit={tile.unit}
          levelSeverity={tile.levelSeverity}
          severityCounts={tile.severityCounts}
          onSelectItem={tile.selectItem}
          nextSteps={crumbs.length === 0 ? nextSteps : undefined}
        />
      </div>
    </AttentionDetailExitProvider>
  )
}

const SinglePartnerExpanded = ({ onCollapse }: { onCollapse: () => void }) => {
  const tile = useCockpitAttentionStatuses()

  return (
    <ExpandedBody
      tile={tile}
      onCollapse={onCollapse}
      detailSection={renderAttentionStatusSection}
      nextSteps="partner"
    />
  )
}

/* The manager cockpit's detail is a PBM's partner, opened with the same sections. */
const ManagerExpanded = ({ onCollapse }: { onCollapse: () => void }) => {
  const tile = useManagerAttentionStatuses()

  return (
    <ExpandedBody
      tile={tile}
      onCollapse={onCollapse}
      detailSection={renderAttentionStatusSection}
      nextSteps="manager"
    />
  )
}

interface ICockpitAttentionStatusesExpandedProps {
  onCollapse: () => void
  /** Manager cockpit: the drill-down walks statuses, geo groups and then partners. */
  isManagerCockpitMode?: boolean
}

/**
 * The tile's detailed state. It shows the level the drill-down stands on, with the way back above
 * it, and the leaf's placeholder section once there is nothing left to drill into. Without a
 * selection it keeps the list, so expanding from the tile control is not a dead end.
 */
export const CockpitAttentionStatusesExpanded = ({
  onCollapse,
  isManagerCockpitMode = false,
}: ICockpitAttentionStatusesExpandedProps) =>
  isManagerCockpitMode ? (
    <ManagerExpanded onCollapse={onCollapse} />
  ) : (
    <SinglePartnerExpanded onCollapse={onCollapse} />
  )

CockpitAttentionStatusesExpanded.displayName = 'CockpitAttentionStatusesExpanded'
